import { useLocalStorage, writeStorage } from '@rehooks/local-storage'
import uniq from 'lodash/uniq'
import { useCallback, useEffect, useMemo } from 'react'

import { NO_COLUMNS } from '../constants/noColumns'
import { getHiddenColumnsState } from '../utils/getHiddenColumnsState'
import { getHiddenColumnsStorageKeys } from '../utils/getHiddenColumnsStorageKeys'

interface UseHiddenColumnsResponse {
  hiddenColumns: string[]
  updateHiddenColumns: (columnId: string, shouldRemove?: boolean) => void
}

/*
 * It gives information from `localStorage` about hidden columns in tables.
 * Due to issues with returning `out-of-sync` data from `useLocalStorage` hook from `react-use` package I replaced it with hook from `@rehooks`.
 * It returns correct data and has nicer syntax. For future features, it makes sense to go with this one.
 * IMPORTANT: This hook doesn't sync between components. It only retrieves newest `hiddenColumns` on initialization of the component.
 * If you want, to keep data in sync then the hook needs to be connected with `Context`.
 *
 * Three keys are stored per table (see `getHiddenColumnsStorageKeys`):
 * - hidden columns: columns the user hid.
 * - default hidden columns: columns that are hidden by default and not switched on by the user yet.
 * - known columns: default-hidden columns the user has already been offered.
 *
 * Migration of storage saved before the known columns key existed: the ids found in the hidden columns and in the
 * default hidden columns, together with every default-hidden column of the table that is not flagged `isNewColumn`
 * (`newColumnsIds`), are known, and are saved as known columns on the first run.
 * Those two keys do not list a default-hidden column the user switched on earlier, so it cannot be told apart from a
 * new default-hidden column. When any older key is saved, a known default-hidden column stays visible if the user had
 * switched it on, while a column flagged `isNewColumn` starts hidden. Flag a default-hidden column only when it is
 * added after users may have saved the table's columns. A table that only saved the hidden columns key (for example
 * because it had no default-hidden columns before) also counts as older storage, so every default-hidden column added
 * to an existing table must be flagged `isNewColumn` to start hidden for existing users.
 * Without any storage (a first-time user) every default-hidden column starts hidden, flagged or not.
 * Known limitation: a non-flagged default-hidden column that the table does not offer on the first visit after the
 * upgrade (for example one shown only under some filters) is not recorded as known. If the user had switched it on, it
 * starts hidden once when it is offered later.
 *
 * The rules are worked out in `getHiddenColumnsState`. This hook only reads and writes the storage. Nothing is written
 * while the saved columns already match, so a table without default-hidden columns saves nothing.
 *
 * Without a `tableId` nothing is written and the saved columns are ignored (their keys would end in `undefined`):
 * default-hidden columns always start hidden and `updateHiddenColumns` does nothing.
 * */
export const useHiddenColumns = (
  tableId?: string,
  defaultHiddenColumnsIds: string[] = NO_COLUMNS,
  columnsIds: string[] = NO_COLUMNS,
  newColumnsIds: string[] = NO_COLUMNS,
): UseHiddenColumnsResponse => {
  const { defaultHiddenColumnsKey, hiddenColumnsKey, knownColumnsKey } = getHiddenColumnsStorageKeys(tableId)

  const [savedHiddenColumns] = useLocalStorage<string[]>(hiddenColumnsKey)
  const [savedDefaultHiddenColumns] = useLocalStorage<string[]>(defaultHiddenColumnsKey)
  const [savedKnownColumns] = useLocalStorage<string[]>(knownColumnsKey)
  const storedHiddenColumns = tableId ? savedHiddenColumns : null
  const storedDefaultHiddenColumns = tableId ? savedDefaultHiddenColumns : null
  const storedKnownColumns = tableId ? savedKnownColumns : null

  const {
    defaultHiddenColumns: nextDefaultHiddenColumns,
    hiddenColumns: combinedHiddenColumns,
    isStorageUpToDate,
    knownColumns: nextKnownColumns,
  } = useMemo(
    () =>
      getHiddenColumnsState({
        columnsIds,
        defaultHiddenColumnsIds,
        newColumnsIds,
        storedDefaultHiddenColumns,
        storedHiddenColumns,
        storedKnownColumns,
      }),
    [
      columnsIds,
      defaultHiddenColumnsIds,
      newColumnsIds,
      storedDefaultHiddenColumns,
      storedHiddenColumns,
      storedKnownColumns,
    ],
  )
  const shouldPersistColumns = !!tableId && !isStorageUpToDate
  const hiddenColumns = useMemo(() => storedHiddenColumns ?? NO_COLUMNS, [storedHiddenColumns])

  const removeColumn = useCallback(
    (columnId: string) => {
      writeStorage(
        hiddenColumnsKey,
        hiddenColumns.filter((hiddenColumnId: string) => hiddenColumnId !== columnId),
      )
      writeStorage(
        defaultHiddenColumnsKey,
        nextDefaultHiddenColumns.filter((defaultHiddenColumnId) => defaultHiddenColumnId !== columnId),
      )
    },
    [defaultHiddenColumnsKey, hiddenColumns, hiddenColumnsKey, nextDefaultHiddenColumns],
  )

  const addColumn = useCallback(
    (columnId: string) => {
      writeStorage(hiddenColumnsKey, uniq([...hiddenColumns, columnId]))
    },
    [hiddenColumns, hiddenColumnsKey],
  )

  const updateHiddenColumns = useCallback(
    (columnId: string, shouldRemove = false) => {
      if (!tableId) {
        return
      }

      if (shouldRemove) {
        removeColumn(columnId)
      } else {
        addColumn(columnId)
      }
    },
    [addColumn, removeColumn, tableId],
  )

  useEffect(() => {
    if (!shouldPersistColumns) {
      return
    }

    writeStorage(defaultHiddenColumnsKey, nextDefaultHiddenColumns)
    writeStorage(knownColumnsKey, nextKnownColumns)
  }, [defaultHiddenColumnsKey, knownColumnsKey, nextDefaultHiddenColumns, nextKnownColumns, shouldPersistColumns])

  return useMemo(
    () => ({
      hiddenColumns: combinedHiddenColumns,
      updateHiddenColumns,
    }),
    [combinedHiddenColumns, updateHiddenColumns],
  )
}
